import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export interface CollectionPin {
  _id: string;
  code: string;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  orderId?: string;
  deliveryId?: string;
  dispatchJobId?: string;
  assignedDriverId?: string;
  status: "active" | "used" | "expired" | "cancelled";
  createdBy?: string;
  expiresAt?: string;
  usedAt?: string;
  requestedAt?: string;
  note?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateCollectionPinPayload {
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  orderId?: string;
  deliveryId?: string;
  dispatchJobId?: string;
  assignedDriverId?: string;
  createdBy?: string;
  expiresAt?: string;
  note?: string;
}

export interface DispatchJob {
  _id: string;
  sourceType?: "order" | "delivery";
  sourceId?: string;
  customerName?: string;
  customerPhone?: string;
  pickupAddress?: string;
  dropoffAddress?: string;
  assignedDriverId?: string;
  status?: string;
  driverEarning?: number;
  orderValue?: number;
  deliveryFee?: number;
  cashOnDelivery?: boolean;
  proofOfDelivery?: {
    required?: boolean;
    pin?: string;
    pinSentToCustomerAt?: string;
    pinVerifiedAt?: string;
    verifiedByDriverId?: string;
    failedAttempts?: number;
  } | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface DriverRow {
  id: string;
  name?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  vehicleType?: string;
  status?: string;
  currentShiftStatus?: string;
  currentJobsCount?: number;
  ratingScore?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateDispatchJobStatusPayload {
  jobId: string;
  status: string;
  cashCollected?: boolean;
  cashCollectedAmount?: number;
  failureReason?: string;
  returnReason?: string;
  notes?: string;
  overrideTransition?: boolean;
}

export interface VerifyDispatchJobPinPayload {
  jobId: string;
  pin: string;
  driverId?: string;
  cashCollected?: boolean;
  cashCollectedAmount?: number;
}

export interface ClaimDispatchJobPayload {
  jobId: string;
  driverId: string;
}

export interface DeleteDispatchJobPayload {
  jobId: string;
}

type ApiResponse<T> = {
  success: boolean;
  data: T;
  message?: string;
};

const PINS_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/pins`;
const DISPATCH_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export const pinManagementApi = createApi({
  reducerPath: "pinManagementApi",
  baseQuery: fetchBaseQuery({ baseUrl: PINS_BASE_URL }),
  tagTypes: ["CollectionPin", "DispatchJob", "Driver"],
  endpoints: (builder) => ({
    getCollectionPins: builder.query<CollectionPin[], void>({
      query: () => "/",
      transformResponse: (response: ApiResponse<CollectionPin[]>) =>
        response.data,
      providesTags: ["CollectionPin"],
    }),

    createCollectionPin: builder.mutation<
      CollectionPin,
      CreateCollectionPinPayload
    >({
      query: (body) => ({
        url: "/",
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<CollectionPin>) =>
        response.data,
      invalidatesTags: ["CollectionPin"],
    }),

    verifyCollectionPin: builder.mutation<CollectionPin, { code: string }>({
      query: (body) => ({
        url: "/verify",
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<CollectionPin>) =>
        response.data,
      invalidatesTags: ["CollectionPin"],
    }),

    resendCollectionPin: builder.mutation<CollectionPin, { pinId: string }>({
      query: ({ pinId }) => ({
        url: `/${pinId}/resend`,
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<CollectionPin>) =>
        response.data,
      invalidatesTags: ["CollectionPin"],
    }),

    assignDriverToPin: builder.mutation<
      CollectionPin,
      { pinId: string; driverId: string }
    >({
      query: ({ pinId, driverId }) => ({
        url: `/${pinId}/assign-driver`,
        method: "POST",
        body: { driverId },
      }),
      transformResponse: (response: ApiResponse<CollectionPin>) =>
        response.data,
      invalidatesTags: ["CollectionPin"],
    }),

    unassignDriverFromPin: builder.mutation<CollectionPin, { pinId: string }>({
      query: ({ pinId }) => ({
        url: `/${pinId}/unassign-driver`,
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<CollectionPin>) =>
        response.data,
      invalidatesTags: ["CollectionPin"],
    }),

    getDispatchJobsForPinManagement: builder.query<DispatchJob[], void>({
      queryFn: async (_arg, _api, _extraOptions, fetchWithBQ) => {
        const response = await fetchWithBQ({
          url: `${DISPATCH_BASE_URL}/dispatch/jobs`,
          method: "GET",
        });

        if (response.error) {
          return { error: response.error as any };
        }

        const data = response.data as ApiResponse<DispatchJob[]>;
        return { data: Array.isArray(data?.data) ? data.data : [] };
      },
      providesTags: ["DispatchJob"],
    }),

    getDrivers: builder.query<DriverRow[], void>({
      queryFn: async (_arg, _api, _extraOptions, fetchWithBQ) => {
        const response = await fetchWithBQ({
          url: `${DISPATCH_BASE_URL}/drivers`,
          method: "GET",
        });

        if (response.error) {
          return { error: response.error as any };
        }

        const data = response.data as ApiResponse<DriverRow[]>;
        return { data: Array.isArray(data?.data) ? data.data : [] };
      },
      providesTags: ["Driver"],
    }),

    claimDispatchJob: builder.mutation<any, ClaimDispatchJobPayload>({
      queryFn: async (
        { jobId, driverId },
        _api,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const response = await fetchWithBQ({
          url: `${DISPATCH_BASE_URL}/dispatch/jobs/${jobId}/claim`,
          method: "POST",
          body: { driverId },
        });

        if (response.error) {
          return { error: response.error as any };
        }

        const data = response.data as ApiResponse<any>;
        return { data: data.data };
      },
      invalidatesTags: ["DispatchJob"],
    }),

    updateDispatchJobStatus: builder.mutation<
      DispatchJob,
      UpdateDispatchJobStatusPayload
    >({
      queryFn: async (
        {
          jobId,
          status,
          cashCollected,
          cashCollectedAmount,
          failureReason,
          returnReason,
          notes,
          overrideTransition,
        },
        _api,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const response = await fetchWithBQ({
          url: `${DISPATCH_BASE_URL}/dispatch/jobs/${jobId}/status`,
          method: "POST",
          body: {
            status,
            cashCollected,
            cashCollectedAmount,
            failureReason,
            returnReason,
            notes,
            overrideTransition,
          },
        });

        if (response.error) {
          return { error: response.error as any };
        }

        const data = response.data as ApiResponse<DispatchJob>;
        return { data: data.data };
      },
      invalidatesTags: ["DispatchJob"],
    }),

    verifyDispatchJobDeliveryPin: builder.mutation<
      DispatchJob,
      VerifyDispatchJobPinPayload
    >({
      queryFn: async (
        { jobId, pin, driverId, cashCollected, cashCollectedAmount },
        _api,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const response = await fetchWithBQ({
          url: `${DISPATCH_BASE_URL}/dispatch/jobs/${jobId}/verify-pin`,
          method: "POST",
          body: {
            pin,
            driverId,
            cashCollected,
            cashCollectedAmount,
          },
        });

        if (response.error) {
          return { error: response.error as any };
        }

        const data = response.data as ApiResponse<DispatchJob>;
        return { data: data.data };
      },
      invalidatesTags: ["DispatchJob"],
    }),

    deleteDispatchJob: builder.mutation<
      { deleted: boolean; jobId: string },
      DeleteDispatchJobPayload
    >({
      queryFn: async ({ jobId }, _api, _extraOptions, fetchWithBQ) => {
        const response = await fetchWithBQ({
          url: `${DISPATCH_BASE_URL}/dispatch/jobs/${jobId}`,
          method: "DELETE",
        });

        if (response.error) {
          return { error: response.error as any };
        }

        const data = response.data as ApiResponse<{
          deleted: boolean;
          jobId: string;
        }>;
        return { data: data.data };
      },
      invalidatesTags: ["DispatchJob", "CollectionPin"],
    }),
  }),
});

export const {
  useGetCollectionPinsQuery,
  useCreateCollectionPinMutation,
  useVerifyCollectionPinMutation,
  useResendCollectionPinMutation,
  useAssignDriverToPinMutation,
  useUnassignDriverFromPinMutation,
  useGetDispatchJobsForPinManagementQuery,
  useGetDriversQuery,
  useClaimDispatchJobMutation,
  useUpdateDispatchJobStatusMutation,
  useVerifyDispatchJobDeliveryPinMutation,
  useDeleteDispatchJobMutation,
} = pinManagementApi;
